import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";

function MapDeviceDialog({ open, onClose, device, onSuccess }) {
  const [searchMerchantId, setSearchMerchantId] = useState("");
  const [searchTerminalId, setSearchTerminalId] = useState("");
  const [vleList, setTerminalList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVle, setSelectedTerminal] = useState(null);
  const api = useApi();

  useEffect(() => {
    if (open) {
      handleSearch();
    } else {
      setSearchMerchantId("");
      setSearchTerminalId("");
      setSelectedTerminal(null);
      setTerminalList([]);
    }
  }, [open]);

  const handleMapDevice = async (terminal) => {
    // console.log("Mapping device to terminal:", terminal);
    try {
      const response = await api.post("devices/map", {
        tid: terminal.tid,
        deviceSerialNo: device.serialNo
      });

      if (response.data.message) {
        toast.success(response.data.message);
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to map device");
    }
  };

  const handleSearch = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setIsLoading(true);
    try {
        const requestData = {
            mid: searchMerchantId,
            msisdn: searchTerminalId,
        };
      const response = await api.post("terminal/browse", requestData);
      if (response.data.body.length > 0) {
        setTerminalList(response.data.body.filter(terminal => terminal.device == null && terminal.deviceType === "Y1000K").map(terminal => ({
          ...terminal,
          id: terminal.tid,
          name: terminal.name
        })));
      } else {
        setTerminalList([]);
      }
    } catch (error) {
      toast.error("Failed to fetch terminal records");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'Terminal ID', 
      flex: 1,
      minWidth: 150 
    },
    { 
      field: 'mid', 
      headerName: 'MID', 
      flex: 1,
      minWidth: 150 
    },
    { 
      field: 'name', 
      headerName: 'Terminal Name', 
      flex: 1.5,
      minWidth: 180 
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <MDButton
          variant="text"
          color="info"
          onClick={() => setSelectedTerminal(params.row)}
        >
          Select
        </MDButton>
      ),
    }
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <MDTypography variant="h6">Map Serial to Terminal</MDTypography>
        <MDTypography variant="subtitle2" color="text">
          Device Serial No: {device?.serialNo}
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={12} sm={5}>
            <MDInput
              label="Search by MID"
              value={searchMerchantId}
              onChange={(e) => setSearchMerchantId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <MDInput
              label="Search by Terminal ID"
              value={searchTerminalId}
              onChange={(e) => setSearchTerminalId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2} style={{ display: 'flex', alignItems: 'center' }}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSearch}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : "Search"}
            </MDButton>
          </Grid>
        </Grid>

        <div style={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={vleList}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            checkboxSelection={false}
            disableSelectionOnClick
            loading={isLoading}
            selectionModel={selectedVle ? [selectedVle.id] : []}
            onRowClick={(params) => setSelectedTerminal(params.row)}
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e0e0e0',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: '#e3f2fd',
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                },
              },
            }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={onClose}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={() => handleMapDevice(selectedVle)}
          color="info"
          variant="gradient"
          disabled={!selectedVle}
        >
          Map Device
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

MapDeviceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  device: PropTypes.shape({
    serialNo: PropTypes.string.isRequired,
    deviceName: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired
  }),
  onSuccess: PropTypes.func
};

export default MapDeviceDialog; 